import {
  CHART_HEIGHT,
  RECHARTS_FULL_WIDTH,
} from "reports/components/Reports/BespokeReports/bespokeReportUtils";
import { PnlBreakdownGettersByProcessGroup } from "reports/components/Reports/BespokeReports/Getters/PnlBreakdownGettersByProcessGroup";
import CardWrapper from "reports/components/Reports/CardWrapperStyles";
import GenericComposedChart, {
  MAX_BAR_SIZE,
  renderCustomBar,
} from "reports/components/Reports/GenericCharts/BarCharts/GenericComposedChart";
import { TopBarLabel } from "reports/components/Reports/RechartHelpers/RechartBarLabels";
import RechartLegend from "reports/components/Reports/RechartHelpers/RechartLegend";
import ReportHeader, {
  getEosDateStringWithGeAndLt,
} from "reports/components/Reports/ReportHeader";
import {
  useGeAndLtFromUrl,
  useProcessGroupAndMachemetryIdList,
} from "reports/components/Reports/reportUtils";
import LoadingCircle from "reports/components/simple/LoadingCircle";
import { brightBlue, darkBlue, unplanned } from "reports/constants/colors";
import {
  PnlBreakdownFilteredWithMultiDataKey,
  PnlBreakdownFiltersEnum,
} from "reports/getters/pnlBreakdownGetter/pnlBreakdownTypes";
import { round } from "lodash";
import { useState } from "react";
import { useParams } from "react-router";
import { Bar, LabelList } from "recharts";
import { ReportParams } from "reports/types";
import styled from "styled-components/macro";
import {
  FORMAT_DATE_NUMBERS,
  getEpochToReadableTime,
  NUM_DAYS_IN_WEEK,
  ONE_DAY_OF_MS,
  ONE_HOUR_OF_MS,
} from "reports/utils/timeUtils";

export const OperatorTPMStyles = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3em;
`;
type ProcessGroupWithColor = {
  name: string;
  color: string;
};

const OPERATOR_TPM = "Operator TPM";

export default function NbOperatorTPM() {
  const { ge, lt } = useGeAndLtFromUrl();
  const { ckey: tenant, shift } = useParams() as ReportParams;

  // Add one day so we start on current day, for last 7 days
  const weekStartTime = ge + ONE_DAY_OF_MS;

  // calculate the past 7 days ge and lt values
  const last7DaysTimeRanges = [...Array(NUM_DAYS_IN_WEEK)].map((_, index) => {
    const ge = weekStartTime - (index + 1) * ONE_DAY_OF_MS;
    const lt = weekStartTime - index * ONE_DAY_OF_MS;
    return { ge, lt };
  });

  const processGroupList = ["Team A", "Team B", "Team C"];
  const processGroupsAndMachemetryIds = useProcessGroupAndMachemetryIdList({
    processGroupList: processGroupList,
  });

  const [data, setData] = useState<PnlBreakdownFilteredWithMultiDataKey[]>([]);

  const operatorTpm = data.map(({ pnlBreakdowns, multiDataKey }) => {
    const shiftBreakdown = pnlBreakdowns.filter(
      pnlBreakdown => pnlBreakdown?.shiftName === shift
    )[0];

    return {
      ge: shiftBreakdown?.ge,
      lt: shiftBreakdown?.lt,
      processGroup: multiDataKey,
      operatorTpmDuration:
        (shiftBreakdown?.planned?.tags.filter(
          ({ tagName }) => tagName === OPERATOR_TPM
        )[0]?.durationMs ?? 0) / ONE_HOUR_OF_MS,
    };
  });

  const getOperatorTpmValueFromGe = ({ geVal }: { geVal: number }) => {
    const valuesWithGe = operatorTpm.filter(({ ge }) => {
      return ge == geVal;
    });

    return valuesWithGe.reduce((acc, { processGroup, operatorTpmDuration }) => {
      acc = { ...acc, [processGroup]: round(operatorTpmDuration, 2) };
      return acc;
    }, {});
  };

  const chartData = last7DaysTimeRanges.map(range => {
    const date = getEpochToReadableTime({
      epochTime: range.ge,
      formatString: FORMAT_DATE_NUMBERS,
    });

    return {
      date,
      ...getOperatorTpmValueFromGe({ geVal: range.ge }),
    };
  });

  const { customBars } = getPnlCustomBars({
    processGroups: [
      { name: "Team A", color: brightBlue },
      { name: "Team B", color: unplanned },
      { name: "Team C", color: darkBlue },
    ],
  });

  const width = RECHARTS_FULL_WIDTH;

  return (
    <>
      {last7DaysTimeRanges.map(({ ge, lt }) => {
        return (
          <PnlBreakdownGettersByProcessGroup
            key={ge}
            ge={ge}
            lt={lt}
            processGroupsAndMachemetryIds={processGroupsAndMachemetryIds}
            setData={setData}
            filter={PnlBreakdownFiltersEnum.shiftName}
          />
        );
      })}
      {data && data.length === NUM_DAYS_IN_WEEK * processGroupList.length ? (
        <>
          <ReportHeader
            title={`Operator TPM Report`}
            subheader={`${tenant.toUpperCase()} - ${shift} Shift`}
            headerDate={getEosDateStringWithGeAndLt(ge, lt)}
          ></ReportHeader>
          <CardWrapper>
            <OperatorTPMStyles>
              <GenericComposedChart
                data={chartData}
                {...{ customBars, width }}
                xLabel={"Date"}
                yLabel={"Hours"}
                height={CHART_HEIGHT}
                legend={RechartLegend({ width })}
              />
            </OperatorTPMStyles>
          </CardWrapper>
        </>
      ) : (
        <LoadingCircle />
      )}
    </>
  );
}

/**
 * Get list of bars and bar objects for Process Groups
 *
 * @return {*}
 *  bars: list of bars
 *  customBars: list of Bar rechart objects with label
 */
function getPnlCustomBars({
  processGroups,
}: {
  processGroups: ProcessGroupWithColor[];
}) {
  const bars = processGroups.map(({ name, color }) => {
    return {
      dataKey: name,
      name,
      fill: color,
      label: false,
    };
  });

  const customBars = bars.map(bar => {
    return (
      <Bar
        key={bar.dataKey}
        {...bar}
        maxBarSize={MAX_BAR_SIZE}
        shape={renderCustomBar(CHART_HEIGHT)}
      >
        <LabelList
          dataKey={bar.dataKey}
          content={TopBarLabel({})}
          fontWeight="bold"
        />
      </Bar>
    );
  });

  return { bars, customBars };
}
